import { zodResolver } from '@hookform/resolvers/zod';
import { formatInTimeZone } from 'date-fns-tz';
import { Sparkles, UserPlus } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CreateCaregiverModal } from '@apps/web/components/caregivers/create_caregiver_modal';
import { CreateClientModal } from '@apps/web/components/clients/create_client_modal';
import {
    formatAddress,
    requiredAddressFieldsSchema,
} from '@apps/web/components/shifts/shared_shift_schema';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import {
    getDateStringInTimezone,
    toDateKey,
    wallClockUtcIso,
} from '@apps/web/lib/date_utils';
import { useMode } from '@apps/web/lib/mode_store';
import {
    useOrganization,
    useOrganizationCaregivers,
    useOrganizationClients,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type TestPatient = {
    readonly address: {
        readonly street: string;
        readonly city: string;
        readonly state: string;
        readonly zip: string;
    };
    readonly carePlan?: string;
};

// keep-sorted start block=yes
const TEST_PATIENTS: Record<string, TestPatient> = {
    'Barbara Thomas': {
        address: {
            street: '321 Example Lane',
            city: 'Test City',
            state: 'NY',
            zip: '10004',
        },
        carePlan: `**Living Situation:** Lives with adult daughter, Sarah in apartment. Sarah is available to assist. No pets. Street parking is available, but the apartment is close to the train.

**Medical History:** End-stage renal disease on dialysis (Monday/Wednesday/Friday), congestive heart failure, diabetes type 2

**Ambulation:** Wheelchair dependent. Requires two-person assist for transfers.

**Allergies:** Latex, adhesive tape

**Pets:** None

**Smoking:** Former smoker, quit 5 years ago

**Care Goals:**
- Support dialysis schedule
- Fluid and dietary compliance
- Skin integrity maintenance
- Quality of life

**Tasks:**
- Two-person assist for all transfers using gait belt (daughter assists when home)
- Complete assistance with bathing, dressing, toileting, and personal hygiene
- Diabetic-appropriate meal preparation following renal diet guidelines
- Strict fluid intake monitoring and logging (maximum 32 oz daily)
- Daily weight check and logging
- Medication administration per schedule (multiple times daily)
- Wound care to stage 2 pressure ulcer on coccyx per nursing instructions
- Transportation coordination to dialysis appointments
- Complete homemaker services including all household tasks
- Monitor for signs of fluid overload or hypoglycemia
- Patient loves discussing music as she was an opera singer`,
    },
    'Elizabeth Taylor': {
        address: {
            street: '789 Sample Blvd',
            city: 'Test City',
            state: 'NY',
            zip: '10003',
        },
        carePlan: `**DNR - COMFORT CARE**

**Living Situation:** Lives alone in senior apartment community with emergency call system.

**Medical History:** Parkinson's disease, osteoarthritis, macular degeneration, anxiety

**Ambulation:** Uses rollator walker with frequent rest breaks. Requires standby assistance due to freezing episodes and shuffling gait.

**Allergies:** None known

**Pets:** No pets, but enjoys bird watching from window.

**Smoking:** Non-smoker

**Care Goals:**
- Maintain safety and dignity at home
- Manage Parkinson's symptoms
- Reduce anxiety and promote calm environment
- Support participation in apartment activities

**Tasks:**
- Standby assistance for all ambulation with awareness of freezing episodes
- Full assistance with tub bath twice weekly including hair washing
- Assistance with dressing, particularly buttons, zippers, and shoes
- Meal preparation with adaptive utensils (weighted fork and spoon)
- Medication administration on strict schedule (timing critical for Parkinson's medications)
- Light hands-on assistance with toileting as needed
- Complete homemaker services
- Fill bird feeders outside window weekly
- Accompany to apartment community activities (bingo, music programs)
- Read mail aloud and assist with correspondence
- Use calm, patient approach during anxiety episodes
- Encourage deep breathing exercises and relaxation techniques`,
    },
    'Jennifer Wilson': {
        address: {
            street: '456 Demo Ave',
            city: 'Test City',
            state: 'NY',
            zip: '10002',
        },
        carePlan: `**HIGH FALL RISK - FULL CODE**

**Living Situation:** Lives with adult daughter in single-level home.

**Medical History:** Hypertension, chronic cellulitis requiring compression stockings, macular degeneration (legally blind), recent subdural hematoma from fall affecting mobility, frequent drop attacks

**Ambulation:** Uses walker with close supervision required at all times due to high fall risk and drop attacks. Wheelchair available for longer distances. Stair lift installed for basement access. Hoyer lift being delivered soon for additional transfer support.

**Allergies:** None known

**Pets:** Two dogs and one cat in home

**Smoking:** Non-smoker

**Care Goals:**
- Fall prevention and safety supervision
- Maximize quality of life despite vision limitations
- Social engagement and mental stimulation
- Support primary caregiver (daughter)
- Encourage safe mobility and participation in activities

**Tasks:**
- Close supervision and standby assistance for all ambulation - high fall risk with drop attacks
- Assist with transfers and ensure use of appropriate equipment (walker, wheelchair)
- Light hands-on assistance as needed for stability
- Application and removal of compression stockings daily
- Assist with prescription glasses and ensure client is wearing them
- Meal preparation with heart-healthy, low-sodium options
- Encourage client to visit kitchen during meal prep to discuss cooking and recipes
- Light housekeeping including dishes, laundry, and maintaining clear walkways
- Companionship focused on conversation about cooking, recipes, and life experiences
- Encourage outdoor time in yard or porch (weather permitting) with close supervision
- Monitor for drop attacks and ensure safe positioning/lowering if one occurs
- Work collaboratively with daughter Cassie who will be available to assist
- Medication reminders as needed
- Be aware of pets and prevent tripping hazards
- Report any changes in mobility, vision complaints, or increase in fall episodes

**Special Considerations:**
- Patient experiences sudden drop attacks - caregiver must stay within arm's reach during ambulation
- Vision severely limited due to macular degeneration - use verbal cues and ensure good lighting
- Daughter is primary caregiver and will collaborate on care
- Hoyer lift coming soon - training will be provided
- Client is very social and enjoys meaningful conversation about her passion for cooking
- Safety is primary concern while maintaining dignity and engagement`,
    },
    'Mary Johnson': {
        address: {
            street: '123 Test Street',
            city: 'Test City',
            state: 'NY',
            zip: '10001',
        },
        carePlan: `**Living Situation:** Lives alone in mobile home. One step to enter.

**Medical History:** Chronic kidney disease stage 4, peripheral neuropathy, legally blind, hearing impaired (uses hearing aids)

**Ambulation:** Independent with cane on level surfaces. Requires contact guard for steps and uneven terrain.

**Allergies:** Codeine, iodine

**Pets:** One medium-sized dog (Beagle mix) - needs daily walk.

**Smoking:** Active smoker - smokes outside only, approximately half pack daily

**Care Goals:**
- Slow kidney disease progression
- Prevent falls related to vision and neuropathy
- Maintain independence
- Proper diabetes foot care

**Tasks:**
- Contact guard assistance for ambulation, especially outdoors and on step
- Setup assistance for bathing with grab bars available
- Assistance with grooming including nail care (cannot see well enough for trimming)
- Low-protein, low-sodium meal preparation per renal diet
- Ensure adequate hydration while monitoring intake
- Medication reminders with verbal confirmation (client cannot see pills clearly)
- Daily foot inspection and documentation of any changes
- Complete housekeeping to maintain clear pathways
- Walk dog daily (15-20 minute walk)
- Accompany client outside for smoking breaks to prevent falls on step
- Read labels, mail, and bills aloud as needed
- Clean hearing aids weekly
- One grocery shopping trip weekly`,
    },
    'Michael Anderson': {
        address: {
            street: '789 Sample Blvd',
            city: 'Test City',
            state: 'NY',
            zip: '10003',
        },
        carePlan: `**FULL CODE - HOSPICE SERVICES IN HOME**

**Living Situation:** Lives with wife in two-story home. Bedroom converted to main floor den. Door code will be provided for access to the home wife is not home.

**Medical History:** ALS (Amyotrophic Lateral Sclerosis), dysphagia requiring pureed diet, uses BiPAP at night

**Ambulation:** Non-ambulatory. Uses power wheelchair operated by hand control. Requires Hoyer lift for all transfers.

**Allergies:** None known

**Pets:** Two small indoor dogs (Yorkies) - wife manages their care.

**Smoking:** Non-smoker

**Care Goals:**
- Maximize comfort and quality of life
- Support wife/primary caregiver
- Maintain dignity
- Prevent complications

**Tasks:**
- Hoyer lift transfers from bed to power wheelchair, to recliner, and back (wife assists)
- Complete assistance with all ADLs including bathing, toileting, and personal hygiene
- Prepare pureed meals and thickened liquids per dysphagia guidelines
- Slow, careful feeding with aspiration precautions (sitting upright, small bites)
- Oral care after each meal and as needed
- Medication administration per schedule
- Range of motion exercises to all extremities twice daily
- Position changes every 2 hours when in bed or recliner
- Skin checks and documentation
- Assist with communication device as needed (speech very limited)
- Suction oral secretions as needed (hospice nurse trains)
- BiPAP setup at bedtime and removal in morning
- Complete bed change and laundry
- Light housekeeping to maintain clean environment
- Provide companionship and read aloud or play music per client preference
- Coordinate with hospice team and communicate changes to wife`,
    },
    'Patricia Davis': {
        address: {
            street: '123 Test Street',
            city: 'Test City',
            state: 'NY',
            zip: '10001',
        },
        carePlan: `**FALL RISK - FULL CODE**

**Living Situation:** Lives alone in a two-story home with bedroom on main floor.

**Medical History:** Rheumatoid arthritis, hypertension, mild cognitive impairment

**Ambulation:** Contact guard assistance with rolling walker for all ambulation. Requires assistance with stairs.

**Allergies:** Penicillin, shellfish

**Pets:** One small dog (Shih Tzu) - needs to be let out to fenced backyard.

**Smoking:** Non-smoker

**Care Goals:**
- Maintain independence in familiar environment
- Medication compliance
- Fall prevention
- Social engagement

**Tasks:**
- Contact guard assistance for ambulation, transfers, and toileting
- Assistance with showering three times weekly, daily sponge bath on other days
- Full meal preparation for breakfast, lunch, and light dinner
- Medication reminders four times daily with compliance tracking
- Light housekeeping including vacuuming, dusting, dishes, and laundry
- Let dog out every 3-4 hours and ensure water bowl is filled
- Assist with managing mail and paying bills as needed
- Encourage participation in puzzle books and TV programs for cognitive stimulation`,
    },
    'Robert Smith': {
        address: {
            street: '456 Demo Ave',
            city: 'Test City',
            state: 'NY',
            zip: '10002',
        },
        carePlan: `**FALL RISK - FULL CODE**

**Living Situation:** Lives alone in 3rd floor walk-up apartment in urban area. No elevator. Street parking only - no dedicated parking available. Entry via lockbox with key code.

**Medical History:** Benign prostatic hyperplasia (BPH), hypertensive heart disease, osteoarthritis in knees, early-stage vascular dementia with short-term memory deficits

**Ambulation:** Independent with cane on level surfaces. Requires contact guard assistance for stairs due to knee arthritis and balance concerns.

**Allergies:** Aspirin

**Pets:** None

**Smoking:** Non-smoker

**Care Goals:**
- Cognitive support and memory compensation strategies
- Maintain independence in apartment
- Stair safety
- Social engagement and mental stimulation
- Medication compliance

**Tasks:**
- Contact guard assistance for stair navigation (3 flights) if client leaves apartment
- NO personal care required - client manages own bathing, dressing, and toileting independently
- Meal preparation for lunch and dinner with focus on heart-healthy, low-sodium options
- Light housekeeping including dishes, vacuuming, dusting, and trash removal
- Medication reminders and setup in daily pill organizer (client self-administers)
- Companionship activities: watching news programs together, working on crossword puzzles, discussing current events
- Gentle reminders for daily hygiene routine and light exercise (client does independently)
- Cognitive engagement through conversation and memory-supporting techniques
- Accompany on errands if needed (note parking challenges - may need to drop off and circle)
- Weekly refrigerator check and grocery list creation
- Monitor for changes in memory or confusion and report to family

**Special Considerations:**
- Caregiver must be comfortable with 3-flight stair climb multiple times
- Allow extra time for parking - street parking only, may need to walk several blocks
- Lockbox key code will be provided for entry
- Client's son visits weekly and has clear expectations for support
- No hands-on personal care - this is companionship with homemaker support`,
    },
    'William Brown': {
        address: {
            street: '321 Example Lane',
            city: 'Test City',
            state: 'NY',
            zip: '10004',
        },
        carePlan: `**Living Situation:** Lives with spouse in single-level ranch home.

**Medical History:** Stroke with right-sided weakness, expressive aphasia, depression

**Ambulation:** Ambulates independently with quad cane for short distances. Uses wheelchair for longer distances.

**Allergies:** Sulfa drugs

**Pets:** One outdoor cat that comes to porch for feeding.

**Smoking:** Non-smoker

**Care Goals:**
- Maximize communication and independence
- Support physical therapy exercises
- Prevent social isolation
- Caregiver respite for spouse

**Tasks:**
- Standby assistance with ambulation, transfers, and ADLs as needed
- Assistance with bathing and dressing (client can do some independently)
- Meal preparation with encouragement to participate in simple tasks
- Support continuation of home exercise program from physical therapy
- Encourage use of communication board and practice speech exercises
- Medication reminders twice daily
- Light housekeeping and laundry
- Feed outdoor cat once daily
- Accompany on walks outside weather permitting (uses wheelchair)
- Provide companionship including cards, music, or looking through photo albums
- One approved outing weekly (library, park, or errands)`,
    },
};
// keep-sorted end

const formSchema = z
    .object({
        patientName: z.string().min(1, 'Patient name is required'),
        ...requiredAddressFieldsSchema,
        startDate: z.string().min(1, 'Start date is required'),
        startTime: z.string().min(1, 'Start time is required'),
        endDate: z.string().min(1, 'End date is required'),
        endTime: z.string().min(1, 'End time is required'),
        frequency: z.enum(['once', 'daily', 'weekly', 'monthly']),
        assignedCaregiverId: z.string().optional(),
        clientId: z.string().optional(),
    })
    .refine(
        (data) => {
            const startDateTime = new Date(
                `${data.startDate}T${data.startTime}`,
            );
            const endDateTime = new Date(`${data.endDate}T${data.endTime}`);
            return endDateTime > startDateTime;
        },
        {
            message: 'End date/time must be after start date/time',
            path: ['endTime'],
        },
    );

type FormValues = z.infer<typeof formSchema>;

type CreateShiftModalProps = {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly onComplete?: (shiftId: string) => void;
    /** Optional date key (YYYY-MM-DD) to use as the default date for the shift */
    readonly initialDateKey?: string;
};

export function CreateShiftModal({
    open,
    onOpenChange,
    onComplete,
    initialDateKey,
}: CreateShiftModalProps) {
    const [showCaregiverModal, setShowCaregiverModal] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const organizationId = useOrganizationId();
    const mode = useMode();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'create-shift-modal',
    });
    const {
        data: clients,
        isLoading: isLoadingClients,
        mutate: mutateClients,
    } = useOrganizationClients(organizationId, {
        reason: 'create-shift-modal',
    });
    const { data: caregivers, mutate: mutateCaregivers } =
        useOrganizationCaregivers(organizationId, {
            reason: 'create-shift-modal',
        });

    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            patientName: '',
            patientStreet: '',
            patientZipCode: '',
            patientCity: '',
            patientState: '',
            startDate: '',
            startTime: '',
            endDate: '',
            endTime: '',
            frequency: 'once',
            assignedCaregiverId: '',
            clientId: '',
        },
    });

    type Client = NonNullable<typeof clients>[number] & {
        readonly address_line_1?: string | null;
        readonly city?: string | null;
        readonly state?: string | null;
        readonly postal_code?: string | null;
        readonly care_plan?: string | null;
    };

    const clientId = form.watch('clientId');
    const selectedClient = clients?.find((client) => client.id === clientId) as
        | Client
        | undefined;
    const patientFieldsReadOnly = !!selectedClient && clientId !== 'unassigned';
    const addressFieldsReadOnly =
        patientFieldsReadOnly && !!selectedClient?.address_line_1;
    const patientFieldClassName = patientFieldsReadOnly
        ? 'bg-muted/50'
        : undefined;
    const addressFieldClassName = addressFieldsReadOnly
        ? 'bg-muted/50'
        : undefined;

    const applyClientToForm = useCallback(
        (client: Client) => {
            form.setValue(
                'patientName',
                client.preferred_name ||
                    `${client.first_name} ${client.last_name}`,
                { shouldValidate: true },
            );
            form.setValue('patientStreet', client.address_line_1 || '', {
                shouldValidate: true,
            });
            form.setValue('patientCity', client.city || '', {
                shouldValidate: true,
            });
            form.setValue('patientState', client.state || '', {
                shouldValidate: true,
            });
            form.setValue('patientZipCode', client.postal_code || '', {
                shouldValidate: true,
            });
        },
        [form],
    );

    const fillTestPatientDefaults = useCallback(
        (testPatient: TestPatient | undefined) => {
            if (!form.getValues('patientStreet') && testPatient) {
                form.setValue('patientStreet', testPatient.address.street, {
                    shouldValidate: true,
                });
                form.setValue('patientCity', testPatient.address.city, {
                    shouldValidate: true,
                });
                form.setValue('patientState', testPatient.address.state, {
                    shouldValidate: true,
                });
                form.setValue('patientZipCode', testPatient.address.zip, {
                    shouldValidate: true,
                });
            }
        },
        [form],
    );

    const getTodayInOrgTimezone = useCallback(() => {
        if (!organization?.timezone) {
            // Fallback to local timezone if org timezone not available
            return new Date().toISOString().split('T')[0];
        }
        return getDateStringInTimezone(new Date(), organization.timezone);
    }, [organization?.timezone]);

    // Reset form when modal closes so fresh defaults are used on next open
    useEffect(() => {
        if (!open) {
            form.reset();
        }
    }, [open, form]);

    // Auto-fill with smart defaults when modal opens
    useEffect(() => {
        if (open) {
            const timezone = organization?.timezone ?? 'UTC';
            const now = new Date();

            const startTime = new Date(now.getTime() + 10 * 60 * 1000);
            const endTime = new Date(now.getTime() + 70 * 60 * 1000);

            // Use initialDateKey if provided, otherwise use today's date
            const startDateStr =
                initialDateKey ?? toDateKey(startTime, timezone);
            const endDateStr = initialDateKey ?? toDateKey(endTime, timezone);
            const startTimeStr = formatInTimeZone(startTime, timezone, 'HH:mm');
            const endTimeStr = formatInTimeZone(endTime, timezone, 'HH:mm');

            form.setValue('startDate', startDateStr, { shouldValidate: true });
            form.setValue('endDate', endDateStr, { shouldValidate: true });
            form.setValue('startTime', startTimeStr, { shouldValidate: true });
            form.setValue('endTime', endTimeStr, { shouldValidate: true });
        }
    }, [open, form, organization?.timezone, initialDateKey]);

    const handleAutoFill = async () => {
        // Prefer selecting an existing client over creating a new one
        const existingClient =
            clients && clients.length > 0
                ? (clients[
                      Math.floor(Math.random() * clients.length)
                  ] as Client)
                : undefined;

        const patientNames = Object.keys(TEST_PATIENTS);
        const randomName =
            patientNames[Math.floor(Math.random() * patientNames.length)] ?? '';
        const testPatient = TEST_PATIENTS[randomName];

        if (existingClient) {
            form.setValue('clientId', existingClient.id, {
                shouldValidate: true,
            });
            applyClientToForm(existingClient);
        } else if (organizationId && testPatient) {
            const [firstName = '', lastName = ''] = randomName.split(' ');
            try {
                const newClient = await getApiClient().post({
                    url: '/organizations/{organization_id}/clients',
                    pathParams: {
                        organization_id: organizationId,
                    },
                    body: {
                        first_name: firstName,
                        last_name: lastName,
                        address_line_1: testPatient.address.street,
                        city: testPatient.address.city,
                        state: testPatient.address.state,
                        postal_code: testPatient.address.zip,
                        care_plan: testPatient.carePlan ?? null,
                        is_active: true,
                    },
                });
                await mutateClients();
                form.setValue('clientId', newClient.id, {
                    shouldValidate: true,
                });
                applyClientToForm(newClient as Client);
            } catch {
                form.setValue('patientName', randomName, {
                    shouldValidate: true,
                });
            }
        }

        fillTestPatientDefaults(testPatient);

        // If no date/time set, use 30 minutes from now with 1 hour duration
        if (!form.getValues('startDate') || !form.getValues('startTime')) {
            const timezone = organization?.timezone ?? 'UTC';
            const now = new Date();
            const startTime = new Date(now.getTime() + 30 * 60 * 1000);
            const endTime = new Date(now.getTime() + 90 * 60 * 1000);
            const startDateStr =
                initialDateKey ?? toDateKey(startTime, timezone);
            const endDateStr = initialDateKey ?? toDateKey(endTime, timezone);
            const startTimeStr = formatInTimeZone(startTime, timezone, 'HH:mm');
            const endTimeStr = formatInTimeZone(endTime, timezone, 'HH:mm');

            form.setValue('startDate', startDateStr, {
                shouldValidate: true,
            });
            form.setValue('endDate', endDateStr, {
                shouldValidate: true,
            });
            form.setValue('startTime', startTimeStr, {
                shouldValidate: true,
            });
            form.setValue('endTime', endTimeStr, {
                shouldValidate: true,
            });
        }
    };

    const onSubmit = async (values: FormValues) => {
        if (!organizationId) {
            toast.error('Cannot create shift', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            return;
        }

        // Get the organization's timezone, fallback to UTC if not available
        const timezone = organization?.timezone ?? 'UTC';

        // Convert wall-clock times to UTC using the organization's timezone
        const startDateTime = new Date(
            `${values.startDate}T${values.startTime}`,
        );
        const endDateTime = new Date(`${values.endDate}T${values.endTime}`);
        const startUtcIso = wallClockUtcIso(startDateTime, timezone);
        const endUtcIso = wallClockUtcIso(endDateTime, timezone);

        try {
            const shiftData = {
                patient_name: values.patientName.trim(),
                patient_address: formatAddress(values),
                patient_city: values.patientCity || null,
                patient_state: values.patientState || null,
                patient_postal_code: values.patientZipCode || null,
                shift_start_time: startUtcIso,
                shift_end_time: endUtcIso,
                frequency: values.frequency,
                ...(values.assignedCaregiverId &&
                values.assignedCaregiverId !== 'unassigned'
                    ? { assigned_caregiver_id: values.assignedCaregiverId }
                    : {}),
                ...(values.clientId && values.clientId !== 'unassigned'
                    ? { client_id: values.clientId }
                    : {}),
            };

            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/shifts',
                pathParams: { organization_id: organizationId },
                body: shiftData,
            });

            toast.success('Created test shift successfully', {
                persistNotification: true,
                notificationType: 'shift_action',
            });

            form.reset();
            onOpenChange(false);
            onComplete?.(response.id);
        } catch (error) {
            toast.error('Failed to create test shift', {
                persistNotification: true,
                notificationType: 'shift_action',
            });
            console.error(error);
        }
    };

    // Get today's date in YYYY-MM-DD format for min date
    const today = getTodayInOrgTimezone();

    // Get date 30 days from now in organization's timezone for max date
    const getMaxDateInOrgTimezone = () => {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);

        if (!organization?.timezone) {
            return futureDate.toISOString().split('T')[0];
        }
        return getDateStringInTimezone(futureDate, organization.timezone);
    };
    const maxDateStr = getMaxDateInOrgTimezone();

    const handleCaregiverCreated = async (
        newCaregivers: Array<{ id: string; name: string }>,
    ) => {
        await mutateCaregivers();
        form.setValue('assignedCaregiverId', newCaregivers[0]?.id, {
            shouldValidate: true,
        });
        setShowCaregiverModal(false);
    };

    const handleClientCreated = async (newClient: {
        id: string;
        first_name: string;
        last_name: string;
        preferred_name?: string | null;
        address_line_1?: string | null;
        city?: string | null;
        state?: string | null;
        postal_code?: string | null;
        care_plan?: string | null;
    }) => {
        const updatedResponse = await mutateClients();
        const updatedClients = updatedResponse?.clients ?? clients;
        form.setValue('clientId', newClient.id, { shouldValidate: true });
        const matchedClient =
            (updatedClients?.find((client) => client.id === newClient.id) as
                | Client
                | undefined) ??
            (clients?.find((client) => client.id === newClient.id) as
                | Client
                | undefined);

        if (matchedClient) {
            applyClientToForm(matchedClient);
        } else {
            form.setValue(
                'patientName',
                newClient.preferred_name ||
                    `${newClient.first_name} ${newClient.last_name}`,
                { shouldValidate: true },
            );
            form.setValue('patientStreet', newClient.address_line_1 || '', {
                shouldValidate: true,
            });
            form.setValue('patientCity', newClient.city || '', {
                shouldValidate: true,
            });
            form.setValue('patientState', newClient.state || '', {
                shouldValidate: true,
            });
            form.setValue('patientZipCode', newClient.postal_code || '', {
                shouldValidate: true,
            });
        }
        setShowClientModal(false);
    };

    return (
        <React.Fragment>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[525px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center py-2">
                            Create Shift
                        </DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="grid gap-4"
                        >
                            {mode === 'sandbox' && (
                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleAutoFill}
                                        disabled={form.formState.isSubmitting}
                                        className="text-xs"
                                    >
                                        <Sparkles className="mr-1 h-3 w-3" />
                                        Auto-fill with test data
                                    </Button>
                                </div>
                            )}
                            <FormField
                                control={form.control}
                                name="patientName"
                                render={({ field }) => (
                                    <FormItem className="grid grid-cols-4 items-center gap-4">
                                        <FormLabel className="text-right">
                                            Patient Name
                                        </FormLabel>
                                        <div className="col-span-3">
                                            <FormControl>
                                                <Input
                                                    placeholder="John Smith"
                                                    readOnly={
                                                        patientFieldsReadOnly
                                                    }
                                                    className={
                                                        patientFieldClassName
                                                    }
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="clientId"
                                render={({ field }) => (
                                    <FormItem className="grid grid-cols-4 items-center gap-4">
                                        <FormLabel className="text-right">
                                            Client
                                        </FormLabel>
                                        <div className="col-span-3 flex gap-2">
                                            <FormControl>
                                                <Select
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                        if (
                                                            value ===
                                                            'unassigned'
                                                        ) {
                                                            return;
                                                        }
                                                        const client =
                                                            clients?.find(
                                                                (client) =>
                                                                    client.id ===
                                                                    value,
                                                            ) as
                                                                | Client
                                                                | undefined;
                                                        if (!client) {
                                                            return;
                                                        }
                                                        applyClientToForm(
                                                            client,
                                                        );
                                                        fillTestPatientDefaults(
                                                            TEST_PATIENTS[
                                                                `${client.first_name} ${client.last_name}`
                                                            ],
                                                        );
                                                    }}
                                                    value={field.value}
                                                    disabled={isLoadingClients}
                                                >
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue
                                                            placeholder={
                                                                isLoadingClients
                                                                    ? 'Loading clients...'
                                                                    : 'Select a client'
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="unassigned">
                                                            No client
                                                        </SelectItem>
                                                        {clients?.map(
                                                            (client) => (
                                                                <SelectItem
                                                                    key={
                                                                        client.id
                                                                    }
                                                                    value={
                                                                        client.id
                                                                    }
                                                                >
                                                                    {
                                                                        client.first_name
                                                                    }{' '}
                                                                    {
                                                                        client.last_name
                                                                    }
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={() =>
                                                    setShowClientModal(true)
                                                }
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                                title="Create new client"
                                            >
                                                <UserPlus className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="patientStreet"
                                render={({ field }) => (
                                    <FormItem className="grid grid-cols-4 items-center gap-4">
                                        <FormLabel className="text-right">
                                            Street Address
                                        </FormLabel>
                                        <div className="col-span-3">
                                            <FormControl>
                                                <Input
                                                    placeholder="123 Main St"
                                                    readOnly={
                                                        addressFieldsReadOnly
                                                    }
                                                    className={
                                                        addressFieldClassName
                                                    }
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <div className="grid grid-cols-4 items-center gap-4">
                                <FormLabel className="text-right">
                                    Location
                                </FormLabel>
                                <div className="col-span-3 grid grid-cols-3 gap-2">
                                    <FormField
                                        control={form.control}
                                        name="patientCity"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        placeholder="City"
                                                        readOnly={
                                                            addressFieldsReadOnly
                                                        }
                                                        className={
                                                            addressFieldClassName
                                                        }
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="patientState"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        placeholder="ST"
                                                        readOnly={
                                                            addressFieldsReadOnly
                                                        }
                                                        className={
                                                            addressFieldClassName
                                                        }
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="patientZipCode"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        placeholder="12345"
                                                        readOnly={
                                                            addressFieldsReadOnly
                                                        }
                                                        className={
                                                            addressFieldClassName
                                                        }
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <FormLabel className="text-right">
                                    Start
                                </FormLabel>
                                <div className="col-span-3 flex gap-2">
                                    <FormField
                                        control={form.control}
                                        name="startDate"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="date"
                                                        className="w-40"
                                                        min={today}
                                                        max={maxDateStr}
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            const newStartDate =
                                                                e.target.value;
                                                            const endDate =
                                                                form.getValues(
                                                                    'endDate',
                                                                );
                                                            if (
                                                                !endDate ||
                                                                endDate <
                                                                    newStartDate
                                                            ) {
                                                                form.setValue(
                                                                    'endDate',
                                                                    newStartDate,
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="startTime"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="time"
                                                        className="w-28"
                                                        {...field}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <FormMessage />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <FormLabel className="text-right">
                                    End
                                </FormLabel>
                                <div className="col-span-3 flex gap-2">
                                    <FormField
                                        control={form.control}
                                        name="endDate"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="date"
                                                        className="w-40"
                                                        min={
                                                            form.watch(
                                                                'startDate',
                                                            ) || today
                                                        }
                                                        max={maxDateStr}
                                                        {...field}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="endTime"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="time"
                                                        className="w-28"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <FormField
                                control={form.control}
                                name="frequency"
                                render={({ field }) => (
                                    <FormItem className="grid grid-cols-4 items-center gap-4">
                                        <FormLabel className="text-right">
                                            Cadence
                                        </FormLabel>
                                        <div className="col-span-3">
                                            <FormControl>
                                                <Select
                                                    onValueChange={
                                                        field.onChange
                                                    }
                                                    value={field.value}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select frequency" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="once">
                                                            Once
                                                        </SelectItem>
                                                        <SelectItem value="daily">
                                                            Daily
                                                        </SelectItem>
                                                        <SelectItem value="weekly">
                                                            Weekly
                                                        </SelectItem>
                                                        <SelectItem value="monthly">
                                                            Monthly
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormMessage />
                                        </div>
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="assignedCaregiverId"
                                render={({ field }) => (
                                    <FormItem className="grid grid-cols-4 items-center gap-4">
                                        <FormLabel className="text-right">
                                            Caregiver
                                        </FormLabel>
                                        <div className="col-span-3 flex gap-2">
                                            <FormControl>
                                                <Select
                                                    onValueChange={
                                                        field.onChange
                                                    }
                                                    value={field.value}
                                                >
                                                    <SelectTrigger className="flex-1">
                                                        <SelectValue placeholder="Select a caregiver" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="unassigned">
                                                            Unassigned
                                                        </SelectItem>
                                                        {caregivers?.map(
                                                            (caregiver) => (
                                                                <SelectItem
                                                                    key={
                                                                        caregiver.id
                                                                    }
                                                                    value={
                                                                        caregiver.id
                                                                    }
                                                                >
                                                                    {formatCaregiverName(
                                                                        caregiver.name,
                                                                        displayCaregiverNamesLastFirst,
                                                                    )}
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={() =>
                                                    setShowCaregiverModal(true)
                                                }
                                                disabled={
                                                    form.formState.isSubmitting
                                                }
                                                title="Create new caregiver"
                                            >
                                                <UserPlus className="h-4 w-4" />
                                            </Button>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    disabled={form.formState.isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        form.formState.isSubmitting ||
                                        !form.formState.isValid
                                    }
                                >
                                    {form.formState.isSubmitting
                                        ? 'Creating...'
                                        : 'Create'}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <CreateClientModal
                open={showClientModal}
                onOpenChange={setShowClientModal}
                organizationId={organizationId ?? ''}
                onClientCreated={handleClientCreated}
            />
            <CreateCaregiverModal
                open={showCaregiverModal}
                onOpenChange={setShowCaregiverModal}
                onComplete={handleCaregiverCreated}
                maxCaregivers={1}
            />
        </React.Fragment>
    );
}
